import { useMemo, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { OverviewCard } from "./Card";
import AIAssistantModal from "@/components/app/AIAssistantModal";

/**
 * Coach-only widget that ships an aggregated client snapshot to Claude and
 * displays the returned actionable report in a modal.
 *
 * Nothing is sent automatically — the coach must press the button explicitly.
 */

function summarize({ client, measurements, checkinResponses, logs, phases, workouts }) {
  const parts = [];
  parts.push(`Cliente: ${client?.first_name || ""} ${client?.last_name || ""}`.trim() || "Cliente anonimo");
  if (client?.date_of_birth) parts.push(`Data di nascita: ${client.date_of_birth}`);
  if (client?.goal) parts.push(`Obiettivo dichiarato: ${client.goal}`);

  const sortedMeas = [...(measurements || [])].sort((a, b) => (a.date > b.date ? -1 : 1));
  if (sortedMeas.length) {
    const m = sortedMeas[0];
    const prev = sortedMeas[Math.min(3, sortedMeas.length - 1)];
    parts.push(`Ultime misure (${m.date}): peso ${m.weight_kg ?? "n/d"} kg, BF ${m.body_fat_pct ?? "n/d"}%, vita ${m.waist_cm ?? "n/d"} cm`);
    if (prev && prev !== m) {
      parts.push(`Confronto ~1 mese fa (${prev.date}): peso ${prev.weight_kg ?? "n/d"} kg, vita ${prev.waist_cm ?? "n/d"} cm`);
    }
  }

  const activePhase = (phases || []).find((p) => p.status === "in_corso");
  if (activePhase) parts.push(`Fase attiva: ${activePhase.title}`);

  parts.push(`Programmi totali: ${(workouts || []).length} schede`);

  const totalLogs = (logs || []).length;
  const last30 = new Date(); last30.setDate(last30.getDate() - 30);
  const recentLogs = (logs || []).filter((l) => new Date(l.date) >= last30);
  parts.push(`Log totali: ${totalLogs} · Ultimi 30gg: ${recentLogs.length}`);

  // Top 3 exercises by max weight in last 30 days
  const byExercise = new Map();
  recentLogs.forEach((l) => {
    (l.sets || []).forEach((s) => {
      const w = Number(s.weight_kg) || 0;
      const r = Number(s.reps) || 0;
      if (r <= 0) return;
      const prev = byExercise.get(l.exercise_name) || { max: 0, best: null };
      if (w > prev.max) {
        byExercise.set(l.exercise_name, { max: w, best: { w, r, date: l.date } });
      }
    });
  });
  const top = Array.from(byExercise.entries()).sort((a, b) => b[1].max - a[1].max).slice(0, 5);
  if (top.length) {
    parts.push("Top esercizi ultimi 30gg:");
    top.forEach(([name, v]) => {
      parts.push(`  - ${name}: ${v.best.w}kg × ${v.best.r} rep (${v.best.date})`);
    });
  }

  const recentCheckin = [...(checkinResponses || [])].sort((a, b) => (a.week_start > b.week_start ? -1 : 1))[0];
  if (recentCheckin?.answers) {
    const summaryAns = Object.entries(recentCheckin.answers).slice(0, 5).map(([k, v]) => `${k}: ${v}`).join("; ");
    parts.push(`Ultimo check-in (${recentCheckin.week_start}): ${summaryAns}`);
  }

  return parts.join("\n");
}

export function AIProgressAnalysisWidget({ client, measurements, checkinResponses, logs, phases, workouts }) {
  const [running, setRunning] = useState(false);

  const clientSummary = useMemo(
    () => summarize({ client, measurements, checkinResponses, logs, phases, workouts }),
    [client, measurements, checkinResponses, logs, phases, workouts]
  );

  return (
    <>
      <OverviewCard
        title="Analisi AI progressi"
        icon={Sparkle}
        iconColor="text-[#AF52DE]"
        testId="overview-ai-analysis"
      >
        <p className="text-xs text-zinc-400 leading-relaxed mb-3">
          Chiedi a Claude un report sintetico sui progressi di questo cliente basato sui log, misure e check-in raccolti.
        </p>
        <button
          type="button"
          onClick={() => setRunning(true)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#AF52DE] to-[#7B4BE6] hover:from-[#C46EEB] hover:to-[#9066EF] text-white text-sm font-bold active:scale-[0.98] transition-transform"
          data-testid="ai-progress-analysis-run"
        >
          <Sparkle size={14} weight="fill" /> Genera analisi con Claude
        </button>
      </OverviewCard>
      {running && (
        <AIAssistantModal
          endpoint="progress-analysis"
          payload={{ client_summary: clientSummary }}
          title="Analisi progressi"
          onClose={() => setRunning(false)}
        />
      )}
    </>
  );
}
